import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, CircularProgress, Paper, Typography, useTheme } from '@mui/material'
import type { Vehicle } from '@otw/models'
import { getLegStatus, getTruckStatus } from '@otw/models/logistics'
import type { ShipmentPlanVehicleDamage } from '@otw/models/logistics'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { useQueries } from '@tanstack/react-query'
import { apiClient } from '@/core/api/apiClient'
import { useTransporterGroups } from '@/hooks/admin/useTransporterGroups'
import { useLogistics, useRepairTrucksForTrucks } from '@/hooks/logistics/useLogistics'
import { usePickupLocations } from '@/hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'
import { useClientUsers } from '@/hooks/useUsers'
import { useTransporterDrivers } from '@/hooks/admin/useTransporterGroups'
import LegPhotosDialog from '../dialogs/LegPhotosDialog'
import TimelineDialog from '../dialogs/TimelineDialog'
import TransportersLegsGrid, { TransportersLegRow } from '../views/TransportersLegsGrid'
import { useLocation, useNavigate } from 'react-router-dom'

const ALL_COMPANIES_TAB = 'ALL'
const OPTIC_FILTER_VALUE = 'OPTIC'
const ALL_STATUS_FILTER_VALUE = 'ALL'
const SHOP_TRANSPORT_COMPANY_ID = 'shop'
const CUSTOMER_PICKUP_COMPANY_ID = 'customer_pickup'
const NON_COMPANY_IDS = new Set([SHOP_TRANSPORT_COMPANY_ID, CUSTOMER_PICKUP_COMPANY_ID])

const formatPrice = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-'
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const isCompletedLegStatus = (status: string) => status.trim().toLowerCase() === 'completed'

const getElapsedMinutesAndLabel = (timestamp: unknown): { minutes: number; label: string } => {
  if (!timestamp) return { minutes: 0, label: '-' }

  let date: Date | null = null
  if (timestamp instanceof Date) {
    date = timestamp
  } else if (typeof timestamp === 'object' && timestamp !== null) {
    const ts = timestamp as { toDate?: () => Date; toMillis?: () => number }
    if (typeof ts.toDate === 'function') {
      date = ts.toDate()
    } else if (typeof ts.toMillis === 'function') {
      date = new Date(ts.toMillis())
    }
  }

  if (!date || Number.isNaN(date.getTime())) {
    return { minutes: 0, label: '-' }
  }

  const diffMs = Math.max(0, Date.now() - date.getTime())
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) {
    return { minutes: diffMinutes, label: `${diffDays}d ${diffHours % 24}h` }
  }
  if (diffHours > 0) {
    return { minutes: diffMinutes, label: `${diffHours}h ${diffMinutes % 60}m` }
  }
  return { minutes: diffMinutes, label: `${diffMinutes}m` }
}

interface TransportTransportersTabProps {
  searchTerm: string
  customerFilter: string
  statusFilter: string
  flaggedOnly: boolean
}

const TransportTransportersTab: React.FC<TransportTransportersTabProps> = ({
  searchTerm,
  customerFilter,
  statusFilter,
  flaggedOnly,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const { transportTrucksWithVehicles, isLoadingTransportTrucks, updateRepairTruckFlagged, isUpdatingRepairTruckFlagged } =
    useLogistics()
  const { clientUsers } = useClientUsers()
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(ALL_COMPANIES_TAB)
  const [timelineVehicle, setTimelineVehicle] = useState<Vehicle | null>(null)
  const [legPhotosState, setLegPhotosState] = useState<{
    planId: string
    legItems: {
      fromLabel: string
      toLabel: string
      company_id?: string
      to_type?: 'pickup' | 'repair'
      latestStatusNotes?: string
    }[]
    initialLegIndex: number
    vehicle_damage?: ShipmentPlanVehicleDamage | null
  } | null>(null)
  const [flagConfirmRow, setFlagConfirmRow] = useState<TransportersLegRow | null>(null)

  const truckIds = useMemo(
    () => transportTrucksWithVehicles.map((item) => item.truck.id),
    [transportTrucksWithVehicles]
  )
  const repairTrucksMap = useRepairTrucksForTrucks(truckIds)

  const baseRows = useMemo(() => {
    const clientNameById = new Map<string, string>()
    clientUsers.forEach((client) => {
      const fullName = `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim()
      clientNameById.set(client.id, fullName || client.email || client.id)
    })

    const pickupById = new Map(
      (pickupLocations ?? []).map((location) => [location.id, location.contact_name || 'Unknown'])
    )
    const repairById = new Map(
      (repairShopLocations ?? []).map((location) => [location.id, location.name || location.contact_name || 'Unknown'])
    )

    const rows: Array<{
      id: string
      companyId: string
      companyName: string
      vin: string
      stockNumber: string
      assignedDriver: string
      price: string
      driverNotes: string
      legStatus: string
      customerName: string
      fromLocation: string
      toLocation: string
      truckId: string
      vehicle: Vehicle | null
      planId: string
      legIndex: number
      driverId?: string
      priceValue?: number
      planClientUserId?: string
      legItems: {
        fromLabel: string
        toLabel: string
        company_id?: string
        to_type?: 'pickup' | 'repair'
        latestStatusNotes?: string
      }[]
      vehicle_damage?: ShipmentPlanVehicleDamage | null
      legStatusUpdatedAt?: unknown
    }> = []

    transportTrucksWithVehicles.forEach((truckWithVehicle) => {
      truckWithVehicle.shipmentPlans.forEach((plan) => {
        const planClientUserId = plan.client_user_id
        const customerName =
          planClientUserId && planClientUserId !== 'optic' ? clientNameById.get(planClientUserId) || '—' : '—'
        const planLegItems = (plan.shipment_legs ?? []).map((planLeg) => {
          const from =
            planLeg.from_type === 'repair'
              ? repairById.get(planLeg.from_id) || 'Unknown'
              : pickupById.get(planLeg.from_id) || 'Unknown'
          const to =
            planLeg.to_type === 'repair'
              ? repairById.get(planLeg.to_id) || 'Unknown'
              : pickupById.get(planLeg.to_id) || 'Unknown'
          const history = planLeg.status_history ?? []
          const latestStatusNotes = history.length ? history[history.length - 1]?.notes : undefined
          return {
            fromLabel: from,
            toLabel: to,
            company_id: planLeg.company_id,
            to_type: planLeg.to_type,
            latestStatusNotes,
          }
        })

        ;(plan.shipment_legs ?? []).forEach((leg, legIndex) => {
          if (!leg.company_id || NON_COMPANY_IDS.has(leg.company_id)) return
          const legStatus = getLegStatus(leg)
          if (isCompletedLegStatus(legStatus)) return
          const legStatusHistory = leg.status_history ?? []
          const latestLegStatusEntry = legStatusHistory[legStatusHistory.length - 1]
          const fromLabel =
            leg.from_type === 'repair'
              ? repairById.get(leg.from_id) || 'Unknown'
              : pickupById.get(leg.from_id) || 'Unknown'
          const toLabel =
            leg.to_type === 'repair'
              ? repairById.get(leg.to_id) || 'Unknown'
              : pickupById.get(leg.to_id) || 'Unknown'

          rows.push({
            id: `${plan.id}:${legIndex}:${truckWithVehicle.truck.id}`,
            companyId: leg.company_id,
            companyName: leg.company_id,
            vin: truckWithVehicle.truck.vin || truckWithVehicle.vehicle?.vin || '—',
            stockNumber: truckWithVehicle.vehicle?.stock_number || '—',
            assignedDriver: '-',
            price: '-',
            driverNotes: leg.assignment_notes || '-',
            legStatus,
            customerName,
            fromLocation: fromLabel,
            toLocation: toLabel,
            truckId: truckWithVehicle.truck.id,
            vehicle: truckWithVehicle.vehicle,
            planId: plan.id,
            legIndex,
            driverId: leg.driver_id,
            priceValue: leg.estimated_shipping_price,
            planClientUserId,
            legItems: planLegItems,
            vehicle_damage: plan.vehicle_damage ?? undefined,
            legStatusUpdatedAt: latestLegStatusEntry?.updated_at,
          })
        })
      })
    })

    return rows
  }, [transportTrucksWithVehicles, clientUsers, pickupLocations, repairShopLocations])

  const companyIds = useMemo(
    () => [...new Set(baseRows.map((row) => row.companyId))],
    [baseRows]
  )
  const { data: transporterGroupData } = useTransporterGroups(companyIds)
  const driverIds = useMemo(
    () =>
      [
        ...new Set(
          baseRows
            .map((row) => row.driverId)
            .filter((id): id is string => Boolean(id))
        ),
      ],
    [baseRows]
  )
  const { data: driversMap } = useTransporterDrivers(driverIds)
  const planIds = useMemo(
    () => [...new Set(baseRows.map((row) => row.planId))],
    [baseRows]
  )
  const legPhotoStatusQueries = useQueries({
    queries: planIds.map((planId) => ({
      queryKey: ['leg_photos_status', planId],
      queryFn: () => apiClient.portal.admin.getLegPhotosStatus(planId),
      enabled: Boolean(planId),
    })),
  })
  const hasPhotosByPlanId = useMemo(() => {
    const map = new Map<string, boolean>()
    planIds.forEach((planId, idx) => {
      const statusByLeg = legPhotoStatusQueries[idx]?.data?.statusByLeg ?? {}
      const hasAnyLegPhotos = Object.values(statusByLeg).some(
        (legStatus) => legStatus?.hasPreDelivery || legStatus?.hasPostDelivery
      )
      map.set(planId, hasAnyLegPhotos)
    })
    return map
  }, [legPhotoStatusQueries, planIds])

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>()
    companyIds.forEach((id) => {
      const owner = transporterGroupData?.groupOwners?.[id]
      map.set(id, owner?.group_name || owner?.email || id)
    })
    return map
  }, [companyIds, transporterGroupData?.groupOwners])

  const rowsWithCompanyName = useMemo(
    () =>
      baseRows.map((row) => ({
        ...row,
        companyName: companyNameById.get(row.companyId) || row.companyId,
        assignedDriver: row.driverId
          ? (() => {
              const driver = driversMap?.[row.driverId]
              if (!driver) return row.driverId
              const fullName = `${driver.first_name ?? ''} ${driver.last_name ?? ''}`.trim()
              return fullName || driver.email || row.driverId
            })()
          : '-',
        price: formatPrice(row.priceValue),
        driverNotes: row.driverNotes || '-',
        ...(() => {
          const timeInStatus = getElapsedMinutesAndLabel(row.legStatusUpdatedAt)
          return {
            timeInStatus: timeInStatus.label,
            timeInStatusMinutes: timeInStatus.minutes,
          }
        })(),
      })),
    [baseRows, companyNameById, driversMap]
  )

  const rowsAfterPrimaryFilters = useMemo(() => {
    const truckStatusById = new Map(
      transportTrucksWithVehicles.map((item) => [item.truck.id, getTruckStatus(item.truck)] as const)
    )

    return rowsWithCompanyName.filter((row) => {
      const passesCustomerFilter =
        customerFilter === 'ALL' ||
        (customerFilter === OPTIC_FILTER_VALUE &&
          (!row.planClientUserId || row.planClientUserId === 'optic')) ||
        row.planClientUserId === customerFilter
      if (!passesCustomerFilter) return false
      if (statusFilter !== ALL_STATUS_FILTER_VALUE && truckStatusById.get(row.truckId) !== statusFilter) return false

      if (flaggedOnly) {
        const truckWithVehicle = transportTrucksWithVehicles.find((item) => item.truck.id === row.truckId)
        const repairTruck =
          truckWithVehicle?.truck.currentLocationId
            ? repairTrucksMap.get(`${row.truckId}_${truckWithVehicle.truck.currentLocationId}`)
            : null
        if (!repairTruck?.flagged) return false
      }
      return true
    })
  }, [
    customerFilter,
    statusFilter,
    flaggedOnly,
    rowsWithCompanyName,
    repairTrucksMap,
    transportTrucksWithVehicles,
  ])

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    if (!normalizedSearch) return rowsAfterPrimaryFilters
    return rowsAfterPrimaryFilters.filter((row) => {
      const haystack = [row.companyName, row.customerName, row.fromLocation, row.toLocation, row.legStatus]
        .concat([row.vin, row.stockNumber, row.assignedDriver, row.price, row.driverNotes])
        .join(' ')
        .toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  }, [rowsAfterPrimaryFilters, searchTerm])

  const companyTabs = useMemo(() => {
    const ids = [...new Set(rowsAfterPrimaryFilters.map((row) => row.companyId))]
    const sorted = ids.sort((a, b) =>
      (companyNameById.get(a) || a).localeCompare(companyNameById.get(b) || b)
    )
    return [
      { id: ALL_COMPANIES_TAB, label: 'All' },
      ...sorted.map((id) => ({ id, label: companyNameById.get(id) || id })),
    ]
  }, [rowsAfterPrimaryFilters, companyNameById])

  useEffect(() => {
    if (selectedCompanyId === ALL_COMPANIES_TAB) return
    if (companyTabs.some((tab) => tab.id === selectedCompanyId)) return
    setSelectedCompanyId(ALL_COMPANIES_TAB)
  }, [companyTabs, selectedCompanyId])

  const visibleRows = useMemo(() => {
    const rowsForCompany =
      selectedCompanyId === ALL_COMPANIES_TAB
        ? filteredRows
        : filteredRows.filter((row) => row.companyId === selectedCompanyId)
    return rowsForCompany.map((row) => ({
      id: row.id,
      companyId: row.companyId,
      companyName: row.companyName,
      vin: row.vin,
      stockNumber: row.stockNumber,
      assignedDriver: row.assignedDriver,
      price: row.price,
      driverNotes: row.driverNotes,
      legStatus: row.legStatus,
      timeInStatus: row.timeInStatus,
      timeInStatusMinutes: row.timeInStatusMinutes,
      customerName: row.customerName,
      fromLocation: row.fromLocation,
      toLocation: row.toLocation,
      hasPhotos: Boolean(hasPhotosByPlanId.get(row.planId)),
    }))
  }, [filteredRows, hasPhotosByPlanId, selectedCompanyId])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const openLegPhotos =
      params.get('openLegPhotos') === '1' || params.get('openPhotos') === '1'
    const vehicleId = params.get('vehicleId')
    const packageId = params.get('packageId')

    if (!openLegPhotos || !vehicleId || rowsWithCompanyName.length === 0) return

    const matchingRow = rowsWithCompanyName.find((row) => {
      const rowVehicleId = row.vehicle?.id
      if (!rowVehicleId || rowVehicleId !== vehicleId) return false

      // Keep backward compatibility for legacy links that include packageId.
      if (!packageId) return true
      return row.planId === packageId || row.id.startsWith(`${packageId}:`)
    })

    if (!matchingRow) return

    setSelectedCompanyId(matchingRow.companyId)
    setLegPhotosState({
      planId: matchingRow.planId,
      legItems: matchingRow.legItems,
      initialLegIndex: matchingRow.legIndex,
      vehicle_damage: matchingRow.vehicle_damage ?? undefined,
    })

    params.delete('openLegPhotos')
    params.delete('openPhotos')
    params.delete('packageId')
    params.delete('vehicleId')
    params.delete('photoTab')
    const nextSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    )
  }, [location.pathname, location.search, navigate, rowsWithCompanyName])

  const getRepairTruckForRow = (row: TransportersLegRow) => {
    const fullRow = rowsWithCompanyName.find((item) => item.id === row.id)
    if (!fullRow) return null
    const truckWithVehicle = transportTrucksWithVehicles.find((item) => item.truck.id === fullRow.truckId)
    if (!truckWithVehicle?.truck.currentLocationId) return null
    return repairTrucksMap.get(`${fullRow.truckId}_${truckWithVehicle.truck.currentLocationId}`) || null
  }
  const repairTruckForFlagConfirm = flagConfirmRow ? getRepairTruckForRow(flagConfirmRow) : null

  if (isLoadingTransportTrucks) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (rowsWithCompanyName.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No legs are currently assigned to transport companies.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.100',
          mb: 2,
          minHeight: 40,
        }}
      >
        {companyTabs.map(({ id, label }) => {
          const isSelected = selectedCompanyId === id
          return (
            <Button
              key={id}
              onClick={() => setSelectedCompanyId(id)}
              sx={{
                minWidth: 100,
                maxWidth: 240,
                px: 1.5,
                textTransform: 'none',
                borderRadius: 0,
                borderBottom: isSelected ? 2 : 0,
                borderColor: 'primary.main',
                bgcolor: isSelected ? 'background.paper' : 'transparent',
                color: isSelected ? 'primary.main' : 'text.primary',
                '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
              }}
            >
              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                {label}
              </Typography>
            </Button>
          )
        })}
      </Box>

      {visibleRows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No assigned legs match the current filters.
          </Typography>
        </Paper>
      ) : (
        <TransportersLegsGrid
          rowData={visibleRows}
          isFlagging={isUpdatingRepairTruckFlagged}
          canFlagRow={(row) => Boolean(getRepairTruckForRow(row))}
          canOpenTimeline={(row) =>
            Boolean(rowsWithCompanyName.find((item) => item.id === row.id)?.vehicle)
          }
          canOpenPhotos={(row) => Boolean(rowsWithCompanyName.find((item) => item.id === row.id))}
          isRowFlagged={(row) => Boolean(getRepairTruckForRow(row)?.flagged)}
          onRequestToggleFlag={(row) => setFlagConfirmRow(row)}
          onOpenTimeline={(row) => {
            const fullRow = rowsWithCompanyName.find((item) => item.id === row.id)
            if (!fullRow?.vehicle) return
            setTimelineVehicle(fullRow.vehicle)
          }}
          onOpenPhotos={(row) => {
            const fullRow = rowsWithCompanyName.find((item) => item.id === row.id)
            if (!fullRow) return
            setLegPhotosState({
              planId: fullRow.planId,
              legItems: fullRow.legItems,
              initialLegIndex: fullRow.legIndex,
              vehicle_damage: fullRow.vehicle_damage ?? undefined,
            })
          }}
        />
      )}

      <ConfirmDialog
        open={!!flagConfirmRow}
        title={repairTruckForFlagConfirm?.flagged ? 'Remove flag from this truck?' : 'Flag this truck?'}
        message={
          repairTruckForFlagConfirm?.flagged
            ? 'This will clear the attention flag from the repair truck.'
            : 'This will mark the repair truck for attention.'
        }
        confirmText={repairTruckForFlagConfirm?.flagged ? 'Remove flag' : 'Flag truck'}
        cancelText="Cancel"
        severity="warning"
        onCancel={() => setFlagConfirmRow(null)}
        onConfirm={async () => {
          if (!flagConfirmRow) return
          const repairTruck = getRepairTruckForRow(flagConfirmRow)
          if (!repairTruck) {
            setFlagConfirmRow(null)
            return
          }
          try {
            await updateRepairTruckFlagged({
              repairTruckId: repairTruck.id,
              flagged: !repairTruck.flagged,
            })
          } finally {
            setFlagConfirmRow(null)
          }
        }}
      />

      {timelineVehicle && (
        <TimelineDialog
          open={!!timelineVehicle}
          onClose={() => setTimelineVehicle(null)}
          vehicle={timelineVehicle}
        />
      )}
      <LegPhotosDialog
        open={!!legPhotosState}
        onClose={() => setLegPhotosState(null)}
        planId={legPhotosState?.planId ?? ''}
        legItems={legPhotosState?.legItems ?? []}
        initialLegIndex={legPhotosState?.initialLegIndex}
        vehicle_damage={legPhotosState?.vehicle_damage ?? undefined}
      />
    </Box>
  )
}

export default TransportTransportersTab
